// Both write paths replace the category, so append must re-send stored groups with store_codes intact.
import type {
  PayloadPostMerchflowSaveVariantRestrictions,
  SaveVariantRestrictionGroup,
  SaveVariantRestrictionRule,
} from "@CommonApi/merchflow/save-variant-restrictions";
import type { RemedyRow, RowFormulation } from "./cdtLayout/restrictionRemedy";
import type { PromotionPlan } from "./cdtLayout/restrictionRemedy";
import {
  detectRuleConflicts,
  type RestrictionFixture,
  type RuleConflict,
} from "./variantRestrictionConflicts";
import type { RestrictionPlacement } from "./studioTypes";

export const STUDIO_GROUP_PREFIX = "CDT-STUDIO-";

export const isStudioGroupId = (id: string): boolean => id.startsWith(STUDIO_GROUP_PREFIX);

/** Studio owns the whole prefix; only superseded groups may be dropped. */
export const partitionStudioOwnedGroups = (
  groups: readonly SaveVariantRestrictionGroup[],
): {
  superseded: readonly SaveVariantRestrictionGroup[];
  retained: SaveVariantRestrictionGroup[];
} => {
  const superseded: SaveVariantRestrictionGroup[] = [];
  const retained: SaveVariantRestrictionGroup[] = [];
  for (const group of groups) {
    (isStudioGroupId(group.variant_group_id) ? superseded : retained).push(group);
  }
  return { superseded, retained };
};

export const isStudioOwnedGroup = isStudioGroupId;

const groupIdPart = (part: string): string =>
  part
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const studioGroupIdFor = (
  kind: "BAND" | "PROMOTED",
  variant: string,
  run: number,
  formulation: RowFormulation = "fence",
): string =>
  `${STUDIO_GROUP_PREFIX}${kind}-${groupIdPart(variant)}-R${run}-${groupIdPart(formulation)}`;

/** `unavailable` is not empty — treating a failed read as [] would wipe the category. */
export type ExistingRestrictions =
  | { kind: "read"; groups: readonly SaveVariantRestrictionGroup[] }
  | { kind: "unavailable"; reason: string }
  /** Replace the category outright. A decision, not a failed read. */
  | { kind: "replaceCategory" };

export interface MergeReport {
  kept: number;
  added: number;
  duplicateRulesDropped: number;
  renamed: readonly { from: string; to: string }[];
  replaced: readonly string[];
  groupsScopedToAllStores: number;
}

export type SavePlan =
  | { ok: true; payload: PayloadPostMerchflowSaveVariantRestrictions; report: MergeReport }
  | { ok: false; refusal: string };

const sameRule = (left: SaveVariantRestrictionRule, right: SaveVariantRestrictionRule): boolean =>
  left.variant === right.variant &&
  left.bay_number_rule === right.bay_number_rule &&
  left.shelf_rule === right.shelf_rule &&
  left.condition === right.condition;

export const toSaveRules = (rows: readonly RemedyRow[]): SaveVariantRestrictionRule[] =>
  rows.map((row) => ({
    variant: row.variant,
    bay_number_rule: row.bayNo,
    shelf_rule: row.shelf,
    condition: row.condition,
  }));

export const toStudioGroups = (rows: readonly RemedyRow[]): SaveVariantRestrictionGroup[] => {
  const byGroup = new Map<string, RemedyRow[]>();
  for (const row of rows) {
    byGroup.set(row.variantGroupId, [...(byGroup.get(row.variantGroupId) ?? []), row]);
  }
  return [...byGroup.entries()].map(([variantGroupId, groupRows]) => ({
    variant_group_id: variantGroupId,
    store_codes: [],
    rules: toSaveRules(groupRows),
  }));
};

/** Existing groups carry over; studio-id collisions replace, others rename ours. */
export const planVariantRestrictionSave = (
  categoryId: number | string,
  studioGroups: readonly SaveVariantRestrictionGroup[],
  existing: ExistingRestrictions,
): SavePlan => {
  if (categoryId === "" || categoryId === null || categoryId === undefined) {
    return { ok: false, refusal: "no category id: the save endpoint scopes everything by it" };
  }
  if (existing.kind === "replaceCategory") {
    return planVariantRestrictionSave(categoryId, studioGroups, { kind: "read", groups: [] });
  }
  if (existing.kind === "unavailable") {
    return {
      ok: false,
      refusal:
        `cannot append: ${existing.reason}. Saving would delete every existing restriction ` +
        "and store-group mapping in the category, because both write paths replace rather " +
        "than append. Refusing rather than guessing.",
    };
  }

  // No store rows cannot round-trip: the read means "no stores", the write "all".
  const widened = scopeWidenedGroups(existing.groups);
  if (widened.length > 0) {
    return {
      ok: false,
      refusal:
        `cannot append: ${widened.length} stored group(s) have no store codes: ` +
        `${widened.join(", ")}. The save contract reads an empty store list as every store ` +
        "in the category, so re-saving them would take groups that currently apply to nothing " +
        "and apply them everywhere. Delete or re-scope them first.",
    };
  }

  // Replace only on same studio-minted id with rules: an empty contribution drops nothing.
  const incomingIds = new Set(
    studioGroups.filter((group) => group.rules.length > 0).map((group) => group.variant_group_id),
  );
  const isOwnPreviousWrite = (group: SaveVariantRestrictionGroup): boolean =>
    isStudioGroupId(group.variant_group_id) && incomingIds.has(group.variant_group_id);
  const replaced = existing.groups
    .filter(isOwnPreviousWrite)
    .map((group) => group.variant_group_id);
  const carried = existing.groups.filter((group) => !isOwnPreviousWrite(group));

  const taken = new Set(carried.map((group) => group.variant_group_id));
  const renamed: { from: string; to: string }[] = [];
  let duplicateRulesDropped = 0;

  const mine: SaveVariantRestrictionGroup[] = [];
  for (const group of studioGroups) {
    const rules: SaveVariantRestrictionRule[] = [];
    for (const rule of group.rules) {
      if (rules.some((kept) => sameRule(kept, rule))) {
        duplicateRulesDropped += 1;
        continue;
      }
      rules.push(rule);
    }
    if (rules.length === 0) continue;

    let id = group.variant_group_id;
    if (taken.has(id)) {
      let counter = 2;
      while (taken.has(`${group.variant_group_id}-${counter}`)) counter += 1;
      id = `${group.variant_group_id}-${counter}`;
      renamed.push({ from: group.variant_group_id, to: id });
    }
    taken.add(id);
    mine.push({ ...group, variant_group_id: id, rules });
  }

  return {
    ok: true,
    payload: {
      category_id: String(categoryId),
      groups: [...carried, ...mine],
    },
    report: {
      kept: carried.length,
      added: mine.length,
      duplicateRulesDropped,
      renamed,
      replaced,
      groupsScopedToAllStores: existing.groups.filter(
        (group) => group.store_codes.length === 0 && group.store_filter === undefined,
      ).length,
    },
  };
};

export interface ProposalRow {
  variant: string;
  bays: string;
  shelf: string;
  condition: string;
}

export interface CarriedGroup {
  variantGroupId: string;
  ruleCount: number;
  /** "every store in the category" when no codes and no filter are set. */
  storeScope: string;
}

export interface RestrictionProposal {
  categoryId: string;
  rows: readonly ProposalRow[];
  /** Counted from the rows: a fence genuinely evicts nothing, a reservation does. */
  cost: {
    cells: number;
    siblingVariants: number;
    evictions: number;
    siblingNames: readonly string[];
  };
  carriedThrough: readonly CarriedGroup[];
  conflicts: readonly RuleConflict[];
  /** Rules deleted outright; narrowed and split rules appear in `conflicts`. */
  deletions: readonly {
    groupId: string;
    variant: string;
    bays: string;
    shelf: string;
    condition: string;
  }[];
  /** Studio-owned groups dropped wholesale. Data, not UI: the consent gate must not render it. */
  supersededGroups: readonly {
    groupId: string;
    ruleCount: number;
    rules: readonly { variant: string; bays: string; shelf: string; condition: string }[];
  }[];
  refusals: readonly string[];
  /** null whenever the proposal must not be written. */
  payload: PayloadPostMerchflowSaveVariantRestrictions | null;
  report: MergeReport | null;
}

export interface RestrictionCost {
  cells: number;
  siblingVariants: number;
  evictions: number;
  siblingNames: readonly string[];
}

export interface RestrictionConflictInputs {
  /** Fence cells the rows name, not the band's seats. */
  placement: RestrictionPlacement;
  keptCells?: RestrictionPlacement;
  bandVariant: string;
  shippedVariants: readonly string[];
  fixture: RestrictionFixture;
}

export interface RestrictionContribution {
  rows: readonly RemedyRow[];
  cost: RestrictionCost;
  conflictInputs?: RestrictionConflictInputs;
}

const combineCosts = (
  rows: readonly RemedyRow[],
  costs: readonly RestrictionCost[],
): RestrictionCost => {
  if (costs.length === 1) return costs[0];
  const cells = new Set<string>();
  for (const row of rows) {
    for (const bayText of row.bayNo.split("|")) {
      const bay = bayText.trim();
      if (bay !== "") cells.add(`${bay}|${row.shelf}`);
    }
  }
  const siblingNames = [...new Set(costs.flatMap((cost) => [...cost.siblingNames]))].sort();
  return {
    cells: cells.size,
    siblingVariants: siblingNames.length,
    evictions: costs.reduce((total, cost) => total + cost.evictions, 0),
    siblingNames,
  };
};

/** Each scan sees the previous scan's adjusted groups so bands compose. */
export const proposeRestrictionsForAll = (
  categoryId: number | string,
  contributions: readonly RestrictionContribution[],
  existing: ExistingRestrictions,
): RestrictionProposal => {
  // Supersede FIRST: nothing downstream may reason about groups on their way out.
  const ownership = existing.kind === "read" ? partitionStudioOwnedGroups(existing.groups) : null;
  const supersededGroups = (ownership?.superseded ?? []).map((group) => ({
    groupId: group.variant_group_id,
    ruleCount: group.rules.length,
    rules: group.rules.map((rule) => ({
      variant: rule.variant,
      bays: rule.bay_number_rule,
      shelf: rule.shelf_rule,
      condition: rule.condition as string,
    })),
  }));

  let effective: ExistingRestrictions = ownership
    ? { kind: "read", groups: ownership.retained }
    : existing;
  const conflicts: RuleConflict[] = [];
  const deleted: { groupId: string; rule: SaveVariantRestrictionRule }[] = [];
  for (const contribution of contributions) {
    if (!contribution.conflictInputs || effective.kind !== "read") continue;
    const scan = detectRuleConflicts(
      effective.groups,
      contribution.conflictInputs.placement,
      contribution.conflictInputs.bandVariant,
      contribution.conflictInputs.shippedVariants,
      contribution.conflictInputs.fixture,
      contribution.conflictInputs.keptCells,
    );
    conflicts.push(...scan.conflicts);
    deleted.push(...scan.deleted);
    // Merge against the ADJUSTED groups, so the resolved conflict is re-saved.
    effective = { kind: "read", groups: scan.adjustedGroups };
  }

  const rows = contributions.flatMap((contribution) => [...contribution.rows]);
  const cost = combineCosts(
    rows,
    contributions.map((contribution) => contribution.cost),
  );
  const plan = planVariantRestrictionSave(categoryId, toStudioGroups(rows), effective);
  // A group this save replaces is not "carried through untouched".
  const replacedIds = new Set(plan.ok ? plan.report.replaced : []);
  const carriedThrough: CarriedGroup[] =
    effective.kind === "read"
      ? effective.groups
          .filter((group) => !replacedIds.has(group.variant_group_id))
          .map((group) => ({
            variantGroupId: group.variant_group_id,
            ruleCount: group.rules.length,
            storeScope:
              group.store_codes.length === 0 && group.store_filter === undefined
                ? "every store in the category"
                : group.store_codes.length > 0
                  ? `${group.store_codes.length} store code(s)`
                  : "a store filter",
          }))
      : [];
  const refusals: string[] = [];
  if (!plan.ok) refusals.push(plan.refusal);
  if (rows.length === 0) refusals.push("no rows to add");

  return {
    categoryId: String(categoryId),
    rows: rows.map((row) => ({
      variant: row.variant,
      bays: row.bayNo,
      shelf: row.shelf,
      condition: row.condition,
    })),
    cost,
    carriedThrough,
    conflicts,
    deletions: deleted.map((entry) => ({
      groupId: entry.groupId,
      variant: entry.rule.variant,
      bays: entry.rule.bay_number_rule,
      shelf: entry.rule.shelf_rule,
      condition: entry.rule.condition,
    })),
    supersededGroups,
    refusals,
    payload: plan.ok && rows.length > 0 ? plan.payload : null,
    report: plan.ok ? plan.report : null,
  };
};

export const proposeRestrictions = (
  categoryId: number | string,
  rows: readonly RemedyRow[],
  cost: RestrictionCost,
  existing: ExistingRestrictions,
  conflictInputs?: RestrictionConflictInputs,
): RestrictionProposal =>
  proposeRestrictionsForAll(categoryId, [{ rows, cost, conflictInputs }], existing);

declare const CONSENT_BRAND: unique symbol;

export interface RestrictionConsent {
  readonly [CONSENT_BRAND]: true;
  readonly payload: PayloadPostMerchflowSaveVariantRestrictions;
  readonly proposal: RestrictionProposal;
}

export const agreeToRestrictions = (proposal: RestrictionProposal): RestrictionConsent | null => {
  if (!proposal.payload) return null;
  return { payload: proposal.payload, proposal } as RestrictionConsent;
};

export const declineRestrictions = (): null => null;

export type CommitOutcome = { ok: true; message: string } | { ok: false; error: string };

export const commitRestrictions = async (
  consent: RestrictionConsent,
  save: (payload: PayloadPostMerchflowSaveVariantRestrictions) => Promise<{ message: string }>,
): Promise<CommitOutcome> => {
  try {
    const response = await save(consent.payload);
    return { ok: true, message: response.message };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/** Persist restrictions before generate reads them. */
export const applyRestrictionsThenGenerate = async <T>(
  consent: RestrictionConsent | null,
  save: (payload: PayloadPostMerchflowSaveVariantRestrictions) => Promise<{ message: string }>,
  generate: () => Promise<T>,
): Promise<{ committed: CommitOutcome; generated: T | null }> => {
  if (!consent) {
    return {
      committed: { ok: false, error: "not agreed: nothing was written and nothing was generated" },
      generated: null,
    };
  }
  const committed = await commitRestrictions(consent, save);
  if (!committed.ok) return { committed, generated: null };
  return { committed, generated: await generate() };
};

/** Empty store_codes reads as "no stores" but writes as every store — refuse those groups. */
export const scopeWidenedGroups = (
  groups: readonly SaveVariantRestrictionGroup[],
): readonly string[] =>
  groups
    .filter((group) => group.store_codes.length === 0 && group.store_filter === undefined)
    .map((group) => group.variant_group_id);

export interface RestrictionOffer {
  bandVariant: string;
  anchorSkuId?: string;
  /** Every product owned by this placement rule, used by panel and planogram selection. */
  skuIds: readonly string[];
  /** Decides the card's wording only; the rule is offered either way. */
  treeHolds?: boolean;
  /** Binary promotion that makes a below-top-level band's variant shippable. */
  promotion?: PromotionPlan;
  rows: readonly RemedyRow[];
  cost: {
    cells: number;
    siblingVariants: number;
    evictions: number;
    siblingNames: readonly string[];
  };
  /** Where the band SITS — the consent question, NOT what the rows name. */
  placement: RestrictionPlacement;
  /** Complement of `placement`: what the rows name and conflicts scan against. */
  fence?: RestrictionPlacement;
  shippedVariants: readonly string[];
  fixture: RestrictionFixture;
  /** Offers refused because they carved the same SKUs another way
   *  (`resolvePromotionConflicts`); the loser is dropped from the offer set. */
  supersedes?: readonly { bandVariant: string; skuIds: readonly string[]; reason: string }[];
}
